import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  PromeniRezimProizvodaCommand,
  RezimProizvoda,
  RobeClient,
  StanjeProizvodaUMagacinuDto,
} from '@kodit/core/data-api';
import { AlertService, SharedService } from '@kodit/core/services';
import { ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kodit-rezim-proizvoda',
  templateUrl: './rezim-proizvoda.component.html',
  styleUrls: ['./rezim-proizvoda.component.scss'],
})
export class RezimProizvodaComponent implements OnChanges, OnDestroy {
  private _subs: Subscription = new Subscription();

  @Input() robaId: number;
  @Input() rezim: RezimProizvoda;
  @Output() rezimPromenjen = new EventEmitter<RezimProizvoda>();

  rezimi = RezimProizvoda;
  izabraniRezim: RezimProizvoda;
  opcije = [
    { label: 'Po lageru', value: RezimProizvoda.NA_LAGER },
    { label: 'Po prodaji (fiskalizaciji)', value: RezimProizvoda.PRI_PRODAJI },
  ];

  constructor(
    private _client: RobeClient,
    private _alertService: AlertService,
    private _sharedService: SharedService,
    private _confirmationService: ConfirmationService
  ) {}

  ngOnChanges(): void {
    this.izabraniRezim = this.rezim ?? RezimProizvoda.NA_LAGER;
  }

  onRezimChange(noviRezim: RezimProizvoda) {
    if (noviRezim === this.rezim) {
      return;
    }

    if (noviRezim === RezimProizvoda.PRI_PRODAJI) {
      this._potvrdiPrelazakNaPriProdaji();
      return;
    }

    this._promeniRezim(noviRezim);
  }

  private _potvrdiPrelazakNaPriProdaji() {
    this._subs.add(
      this._client.getStanjeZaPromenuRezima(this.robaId).subscribe({
        next: (res) => {
          this._confirmationService.confirm({
            header: 'Prelazak na režim Po prodaji (fiskalizaciji)',
            message: this._kreirajPoruku(res.data ?? []),
            acceptLabel: 'Potvrdi',
            rejectLabel: 'Odustani',
            rejectButtonStyleClass: 'p-button-outlined',
            accept: () => this._promeniRezim(RezimProizvoda.PRI_PRODAJI),
            reject: () => (this.izabraniRezim = this.rezim),
          });
        },
        error: (err) => {
          this.izabraniRezim = this.rezim;
          this._prikaziGresku(err);
        },
      })
    );
  }

  private _kreirajPoruku(stanja: StanjeProizvodaUMagacinuDto[]): string {
    const napomena =
      'Sastojci ovog proizvoda će se razduživati automatski pri izdavanju fiskalnog računa, a sam proizvod se više neće voditi na lageru i neće biti dostupan na dokumentima veleprodaje i ručnom radnom nalogu.';

    if (stanja.length === 0) {
      return `${napomena}<br/><br/>Ova radnja je nepovratna.`;
    }

    const lista = stanja
      .map(
        (x) =>
          `${x.nazivMagacina}: ${(x.stanje ?? 0).toLocaleString('sr-Latn-RS', {
            maximumFractionDigits: 3,
          })} ${x.jedinicaMere ?? ''}`
      )
      .join('<br/>');

    return `${napomena}<br/><br/>Stanje proizvoda biće svedeno na 0 u svim magacinima:<br/>${lista}<br/><br/>Ova radnja je nepovratna.`;
  }

  private _promeniRezim(noviRezim: RezimProizvoda) {
    this._sharedService.displayLoadingScreen(true, 'Promena režima...');
    this._subs.add(
      this._client
        .promeniRezimProizvoda(
          new PromeniRezimProizvodaCommand({
            predmetStavkeId: this.robaId,
            rezimProizvoda: noviRezim,
          })
        )
        .subscribe({
          next: (res) => {
            this._sharedService.resetLoadingScreen();
            this.rezim = res.data;
            this.izabraniRezim = res.data;
            this._alertService.addSuccessMsg(res.messages?.[0] ?? 'Režim proizvoda je promenjen.');
            this.rezimPromenjen.emit(res.data);
          },
          error: (err) => {
            this._sharedService.resetLoadingScreen();
            this.izabraniRezim = this.rezim;
            this._prikaziGresku(err);
          },
        })
    );
  }

  private _prikaziGresku(err: any) {
    this._subs.add(
      err.subscribe((x: string) => this._alertService.addFailedMsg(x))
    );
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
